import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Chip,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  LinearProgress,
  Fade,
  Slide,
  Zoom,
  IconButton,
  Divider,
  Stack,
} from "@mui/material";
import {
  SettingsRemote as SettingsRemoteIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  DeviceHub as DeviceHubIcon,
  PlayArrow as PlayArrowIcon,
  Refresh as RefreshIcon,
  SkipNext as SkipNextIcon,
  Speed as SpeedIcon,
  Shield as ShieldIcon,
  Visibility as VisibilityIcon,
  Settings as SettingsIcon,
  Sensors,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { gql, useQuery } from "@apollo/client";
import { SOCKET_TIMEOUT } from "../../constants";

// GraphQL Query for sensor settings
const GET_USER_SENSOR_SETTINGS = gql`
  query GetUserSensorSettings {
    getUserSensorSettings {
      id
      debugMode
      sensors {
        id
        name
        position
        isEnabled
        isSkipped
      }
    }
  }
`;

const KitchenFlow = ({
  checkSignals,
  checkUtensil,
  emitWithTimeout,
  tasks,
  isMergedRecipes,
  onFlowComplete,
  flowStage,
  onStageChange,
  performSequentialLidOperations,
  enableSignalCheck = true,
  enableUtensilCheck = true,
  enableDispenserCheck = true,
  dispenserCheckStatus = null,
  hasDispenserTasks = () => false,
  getFirstGrainTask = () => null,
  getFirstSpiceTask = () => null,
  onDispenserRetry = null,
}) => {
  const theme = useTheme();

  // Debug logging for props
  useEffect(() => {
    console.log("🔧 KitchenFlow Props Debug:", {
      enableSignalCheck,
      enableUtensilCheck,
      enableDispenserCheck,
      flowStage,
    });
  }, [enableSignalCheck, enableUtensilCheck, enableDispenserCheck, flowStage]);

  // Get user sensor settings for proper sensor names
  const { data: sensorSettingsData } = useQuery(GET_USER_SENSOR_SETTINGS, {
    errorPolicy: "ignore",
  });

  // STATE DECLARATIONS - MOVED TO BEGINNING TO AVOID TEMPORAL DEAD ZONE
  // State for enhanced signal checking
  const [enhancedSignalCheck, setEnhancedSignalCheck] = useState({
    isChecking: false,
    currentSensorIndex: -1,
    sensorResults: [],
    completed: false,
  });

  // State for signals check
  const [checkingSignals, setCheckingSignals] = useState(false);
  const [signalsResult, setSignalsResult] = useState(null);
  const [signalsError, setSignalsError] = useState(null);
  const [showDetailedSignals, setShowDetailedSignals] = useState(false);

  // State for utensil check
  const [checkingUtensils, setCheckingUtensils] = useState(false);
  const [utensilResults, setUtensilResults] = useState({});
  const [utensilErrors, setUtensilErrors] = useState({});
  const [lidCloseStatus, setLidCloseStatus] = useState({});
  const [closingLids, setClosingLids] = useState(false);

  // State for security checks
  const [securityChecks, setSecurityChecks] = useState([]);
  const [completedSecurityChecks, setCompletedSecurityChecks] = useState(
    new Set()
  );

  // Determine which pots are needed
  const hasNP1 = tasks.some((t) => t.cookIn === "np1");
  const hasNP2 = tasks.some((t) => t.cookIn === "np2");
  const noPotsNeeded = !hasNP1 && !hasNP2;

  // System Header Component (Simplified)
  const SystemHeader = () => (
    <Fade in={true} timeout={1000}>
      <Card
        sx={{
          mb: 3,
          background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
          border: `1px solid ${theme.palette.grey[300]}`,
          borderRadius: 2,
        }}
      >
        <CardContent sx={{ pb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <SettingsIcon
              sx={{
                mr: 2,
                fontSize: 28,
                color: theme.palette.primary.main,
              }}
            />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
              }}
            >
              Kitchen System Check
            </Typography>
            <Box sx={{ ml: "auto" }}>
              <Chip
                label={getSystemStatus()}
                color={getSystemStatusColor()}
                sx={{ fontWeight: 600 }}
              />
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary">
            Verifying all kitchen components and safety systems...
          </Typography>
        </CardContent>
      </Card>
    </Fade>
  );

  // Get system status
  const getSystemStatus = () => {
    if (flowStage === "checking-signals" && checkingSignals)
      return "CHECKING SIGNALS";
    if (flowStage === "checking-utensils" && checkingUtensils)
      return "CHECKING UTENSILS";
    if (flowStage === "checking-dispensers" && dispenserCheckStatus?.isChecking)
      return "CHECKING DISPENSERS";
    if (flowStage === "security-checks") return "AWAITING CONFIRMATION";
    if (signalsResult?.success) return "SYSTEMS READY";
    return "INITIALIZING";
  };

  const getSystemStatusColor = () => {
    if (flowStage === "security-checks") return "success";
    if (signalsResult?.success) return "success";
    if (checkingSignals || checkingUtensils || dispenserCheckStatus?.isChecking)
      return "primary";
    return "default";
  };

  // Helper function to get sensor name by position
  const getSensorNameByPosition = useCallback(
    (position) => {
      if (!sensorSettingsData?.getUserSensorSettings?.sensors) {
        const defaultNames = {
          1: "Masala Bowl",
          2: "Grain Bowl",
          3: "Manual Cup 1",
          4: "Manual Cup 2",
          5: "Manual Cup 3",
          6: "Manual Cup 4",
          7: "Manual Bowl 1",
          8: "Manual Bowl 2",
          9: "Manual Bowl 3",
          10: "Manual Bowl 4",
          11: "Manual Bowl 5",
          12: "Manual Bowl 6",
          13: "Spatula",
          14: "Water Bottle",
          15: "Soak Bowl",
          16: "Oil Bottle 1",
          17: "Oil Bottle 2",
          18: "Sauce Bottle 1",
          19: "Sauce Bottle 2",
          20: "Lid 1 Close",
          21: "Lid 2 Close",
        };
        return defaultNames[position] || `Sensor ${position}`;
      }

      const sensor = sensorSettingsData.getUserSensorSettings.sensors.find(
        (s) => s.position === position
      );
      return sensor ? sensor.name : `Sensor ${position}`;
    },
    [sensorSettingsData]
  );

  // Enhanced signal checking with animation
  const checkSignalsWithAnimation = useCallback(async () => {
    try {
      setEnhancedSignalCheck({
        isChecking: true,
        currentSensorIndex: 0,
        sensorResults: [],
        completed: false,
      });

      // Get sensor configurations
      const userSettings = sensorSettingsData?.getUserSensorSettings;
      const sensorConfigs = userSettings?.sensors || [];

      // Filter enabled sensors
      const enabledSensors = sensorConfigs.filter(
        (sensor) => sensor.isEnabled && !sensor.isSkipped
      );

      console.log("🔍 Starting enhanced signal check with animation...");
      console.log("📊 Checking", enabledSensors.length, "enabled sensors");

      // Get raw signals from socket
      const response = await checkSignals();
      console.log("📡 Received signal response:", response);

      // Create sensor results with animation
      const results = [];
      for (let i = 0; i < enabledSensors.length; i++) {
        const sensor = enabledSensors[i];

        // Update current checking sensor
        setEnhancedSignalCheck((prev) => ({
          ...prev,
          currentSensorIndex: i,
        }));

        // Simulate checking delay for animation
        await new Promise((resolve) => setTimeout(resolve, 800));

        // Determine if this sensor passed or failed
        const sensorPassed = !response.failedSensors.some(
          (failed) => failed.position === sensor.position
        );

        const result = {
          position: sensor.position,
          name: sensor.name,
          status: sensorPassed ? "passed" : "failed",
          reason: sensorPassed ? "Signal OK - GREEN" : "No signal detected",
        };

        results.push(result);

        // Update results progressively
        setEnhancedSignalCheck((prev) => ({
          ...prev,
          sensorResults: [...results],
        }));

        console.log(
          `📍 Sensor ${sensor.position} (${sensor.name}): ${result.status}`
        );
      }

      // Mark as completed
      setEnhancedSignalCheck((prev) => ({
        ...prev,
        currentSensorIndex: -1,
        completed: true,
        isChecking: false,
      }));

      console.log("✅ Enhanced signal check completed");
      return response;
    } catch (error) {
      console.error("❌ Enhanced signal check failed:", error);
      setEnhancedSignalCheck((prev) => ({
        ...prev,
        isChecking: false,
        currentSensorIndex: -1,
        completed: true,
      }));
      throw error;
    }
  }, [checkSignals, sensorSettingsData]);

  // Enhanced Signal Display Component
  const EnhancedSignalDisplay = () => {
    // Only show when actively checking signals
    if (!enhancedSignalCheck.isChecking) {
      return null;
    }

    return (
      <Card sx={{ mt: 2, mb: 2 }}>
        <CardContent>
          <Typography
            variant="h6"
            sx={{ mb: 2, display: "flex", alignItems: "center" }}
          >
            <CircularProgress size={20} sx={{ mr: 1 }} />
            Checking Sensors...
          </Typography>

          {enhancedSignalCheck.sensorResults.length > 0 && (
            <List dense>
              {enhancedSignalCheck.sensorResults.map((sensor, index) => (
                <ListItem
                  key={sensor.position}
                  sx={{
                    py: 0.5,
                    backgroundColor:
                      index === enhancedSignalCheck.currentSensorIndex
                        ? theme.palette.action.selected
                        : "transparent",
                    borderRadius: 1,
                    transition: "background-color 0.3s ease",
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {index === enhancedSignalCheck.currentSensorIndex ? (
                      <CircularProgress size={16} />
                    ) : sensor.status === "passed" ? (
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: "50%",
                          bgcolor: "success.main",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 10,
                          color: "white",
                        }}
                      >
                        ✓
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: "50%",
                          bgcolor: "error.main",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 10,
                          color: "white",
                        }}
                      >
                        ✗
                      </Box>
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={`${sensor.name} (Position ${sensor.position})`}
                    secondary={sensor.reason}
                    sx={{
                      "& .MuiListItemText-primary": {
                        fontWeight:
                          index === enhancedSignalCheck.currentSensorIndex
                            ? 600
                            : 400,
                      },
                    }}
                  />
                  {index === enhancedSignalCheck.currentSensorIndex && (
                    <Chip
                      label="Checking..."
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    );
  };

  // Generate security checks based on tasks and results
  useEffect(() => {
    const generateSecurityChecks = () => {
      const checks = [];

      // Chimney check
      const hasChimneyOn = tasks.some((t) => t.name === "Chimney On");
      const hasChimneyOff = tasks.some((t) => t.name === "Chimney Off");
      if (hasChimneyOn) {
        checks.push({
          id: "chimney_check",
          message: hasChimneyOff
            ? "I confirm chimney is properly OFF before starting"
            : "This recipe doesn't include chimney OFF, are you sure?",
          critical: !hasChimneyOff,
        });
      }

      // Lid check
      const hasOpenLid = tasks.some((t) => t.name === "Open Lid");
      const hasPourActions = tasks.some((t) => t.category === "Pour Action");

      if (hasOpenLid) {
        checks.push({
          id: "lid_closed",
          message: "I confirm the lid is CLOSED before starting",
          critical: true,
        });
      }

      if (!hasOpenLid && hasPourActions) {
        checks.push({
          id: "lid_open_check",
          message:
            "Pour actions detected without lid opening, confirm manual lid opening",
          critical: true,
        });
      }

      // Pot utensil check
      if (hasNP1 || hasNP2) {
        checks.push({
          id: "nutripot_check",
          message:
            "I confirm Pot utensil is properly placed and securely attached",
          critical: true,
        });
      }

      // Green signal check
      checks.push({
        id: "green_signal",
        message: "I confirm all kitchen lights are GREEN and ready",
        critical: true,
      });

      checks.push({
        id: "kitchen_ready",
        message: "I confirm the kitchen is ready for cooking",
        critical: true,
      });

      return checks;
    };

    setSecurityChecks(generateSecurityChecks());
  }, [tasks, hasNP1, hasNP2]);

  // Auto-confirmation when entering security-checks stage
  useEffect(() => {
    if (flowStage === "security-checks" && securityChecks.length > 0) {
      const newCompleted = new Set(completedSecurityChecks);
      let hasChanges = false;

      securityChecks.forEach((check) => {
        if (!newCompleted.has(check.id)) {
          let shouldAutoConfirm = false;

          switch (check.id) {
            case "green_signal":
              if (
                signalsResult?.success ||
                signalsResult?.failedSensors?.length === 0
              ) {
                shouldAutoConfirm = true;
              }
              break;
            case "nutripot_check":
              if (
                Object.values(utensilResults).some((r) => r === true) ||
                noPotsNeeded
              ) {
                shouldAutoConfirm = true;
              }
              break;
            case "lid_closed":
              if (
                Object.values(lidCloseStatus).some((s) => s === "success") ||
                signalsResult?.success ||
                Object.keys(lidCloseStatus).length === 0
              ) {
                shouldAutoConfirm = true;
              }
              break;
          }

          if (shouldAutoConfirm) {
            newCompleted.add(check.id);
            hasChanges = true;
          }
        }
      });

      if (hasChanges) {
        setCompletedSecurityChecks(newCompleted);
      }
    }
  }, [
    flowStage,
    securityChecks,
    signalsResult,
    utensilResults,
    lidCloseStatus,
    noPotsNeeded,
    completedSecurityChecks,
  ]);

  // Step 1: Check Signals
  const handleCheckSignals = useCallback(async () => {
    try {
      setCheckingSignals(true);
      setSignalsError(null);
      console.log("🔍 Starting signals check...");

      const response = await checkSignalsWithAnimation();
      console.log("📡 Signals check response:", response);

      // Process the response to include proper sensor names
      const processedResponse = {
        ...response,
        passedSensors:
          response.passedSensors?.map((sensor) => ({
            ...sensor,
            name: sensor.name || getSensorNameByPosition(sensor.position),
          })) || [],
        failedSensors:
          response.failedSensors?.map((sensor) => ({
            ...sensor,
            name: sensor.name || getSensorNameByPosition(sensor.position),
            reason: sensor.reason || "Signal not detected",
          })) || [],
        allSensors:
          response.allSensors?.map((sensor) => ({
            ...sensor,
            name: sensor.name || getSensorNameByPosition(sensor.position),
          })) || [],
      };

      setSignalsResult(processedResponse);
      setShowDetailedSignals(true);
      setCheckingSignals(false);

      // Auto-progress if ALL signals are successful
      if (
        processedResponse.success &&
        (!processedResponse.failedSensors ||
          processedResponse.failedSensors.length === 0)
      ) {
        console.log("✅ All signals successful, auto-progressing...");
        setTimeout(() => {
          if (noPotsNeeded) {
            onStageChange("security-checks");
          } else {
            onStageChange("checking-utensils");
          }
        }, 3000);
      }
    } catch (err) {
      console.error("❌ Error checking signals:", err);
      setSignalsError(err.message || "Failed to check kitchen signals");
      setSignalsResult({
        success: false,
        message: err.message,
        failedSensors: [],
        passedSensors: [],
        totalSignals: 0,
        passedSignals: 0,
      });
      setCheckingSignals(false);
    }
  }, [
    checkSignalsWithAnimation,
    noPotsNeeded,
    onStageChange,
    getSensorNameByPosition,
  ]);

  // Step 2: Check Utensils with sequential operations for merged recipes
  const handleCheckUtensils = useCallback(
    async (utensilType) => {
      try {
        setCheckingUtensils(true);
        console.log(`🔍 Starting utensil check for: ${utensilType}`);

        // This function is now only used for single recipes
        // Merged recipes use the sequential operations in useEffect
        console.log("🔧 Using individual lid operations for single recipe...");

        // Step 1: Open lid
        console.log(`🔓 Opening lid for ${utensilType}...`);
        setLidCloseStatus((prev) => ({ ...prev, [utensilType]: "opening" }));

        try {
          const openCode = `async function start() {
  this.utensil = '${utensilType}';
  await lidOpen('${utensilType}');
}
start();`;
          console.log(
            `📡 Sending lid open command for ${utensilType}:`,
            openCode
          );
          const openResponse = await emitWithTimeout(
            "cook-now",
            openCode,
            SOCKET_TIMEOUT
          );
          console.log(`📡 Lid open response for ${utensilType}:`, openResponse);

          if (openResponse === true) {
            console.log(`✅ Lid opened for ${utensilType}`);
            setLidCloseStatus((prev) => ({ ...prev, [utensilType]: "opened" }));
          } else {
            console.error(
              `❌ Lid open failed for ${utensilType} - expected true, got:`,
              openResponse
            );
            setLidCloseStatus((prev) => ({
              ...prev,
              [utensilType]: "open-failed",
            }));
          }
        } catch (openErr) {
          console.error(`❌ Error opening lid for ${utensilType}:`, openErr);
          setLidCloseStatus((prev) => ({
            ...prev,
            [utensilType]: "open-failed",
          }));
        }

        // Step 2: Check utensil with 60-second timeout
        console.log(`🔍 Checking utensil: ${utensilType}`);

        let isDetected = false;
        let utensilCheckError = null;

        try {
          // Create a promise that resolves with the utensil check result
          const utensilCheckPromise = checkUtensil(utensilType);

          // Create a timeout promise that rejects after 60 seconds
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => {
              reject(new Error("Utensil check timeout after 60 seconds"));
            }, 60000); // 60 seconds
          });

          // Race between utensil check and timeout
          isDetected = await Promise.race([
            utensilCheckPromise,
            timeoutPromise,
          ]);
          console.log(`📡 Utensil ${utensilType} detected:`, isDetected);
        } catch (error) {
          console.error(`❌ Utensil check failed for ${utensilType}:`, error);

          if (error.message.includes("timeout")) {
            console.log(
              `⏰ Utensil check timed out for ${utensilType} after 60 seconds`
            );
            utensilCheckError = "Utensil check timed out after 60 seconds";
          } else {
            utensilCheckError = error.message || "Utensil check failed";
          }

          isDetected = false;
        }

        setUtensilResults((prev) => ({ ...prev, [utensilType]: isDetected }));

        // Set error if any occurred
        if (utensilCheckError) {
          setUtensilErrors((prev) => ({
            ...prev,
            [utensilType]: utensilCheckError,
          }));
        }

        // Step 3: Close lid for safety
        console.log(`🔒 Closing lid for ${utensilType}...`);
        setClosingLids(true);

        try {
          const closeCode = `async function start() {
  this.utensil = '${utensilType}';
  await lidClose('${utensilType}');
}
start();`;
          console.log(
            `📡 Sending lid close command for ${utensilType}:`,
            closeCode
          );
          const closeResponse = await emitWithTimeout(
            "cook-now",
            closeCode,
            SOCKET_TIMEOUT
          );
          console.log(
            `📡 Lid close response for ${utensilType}:`,
            closeResponse
          );

          if (closeResponse === true) {
            setLidCloseStatus((prev) => ({
              ...prev,
              [utensilType]: "success",
            }));

            if (utensilCheckError) {
              console.log(
                `✅ Lid closed for ${utensilType} after utensil check error: ${utensilCheckError}`
              );
            } else {
              console.log(`✅ Lid closed successfully for ${utensilType}`);
            }
          } else {
            console.error(
              `❌ Lid close failed for ${utensilType} - expected true, got:`,
              closeResponse
            );
            setLidCloseStatus((prev) => ({ ...prev, [utensilType]: "failed" }));
          }
        } catch (closeErr) {
          console.error(`❌ Error closing lid for ${utensilType}:`, closeErr);
          setLidCloseStatus((prev) => ({ ...prev, [utensilType]: "failed" }));
        }

        setClosingLids(false);
        setCheckingUtensils(false);
      } catch (err) {
        console.error(`❌ Error checking utensil ${utensilType}:`, err);
        setUtensilErrors((prev) => ({ ...prev, [utensilType]: err.message }));
        setUtensilResults((prev) => ({ ...prev, [utensilType]: false }));
        setLidCloseStatus((prev) => ({ ...prev, [utensilType]: "error" }));
        setCheckingUtensils(false);
        setClosingLids(false);
      }
    },
    [checkUtensil, emitWithTimeout]
  );

  // Start initial checks when stage changes - MOVED AFTER handleCheckUtensils definition
  useEffect(() => {
    if (flowStage === "checking-signals") {
      if (!enableSignalCheck) {
        console.log("⏭️ Signal check disabled in settings, skipping...");
        // Skip to next stage
        if (!enableUtensilCheck || noPotsNeeded) {
          // Check if dispenser check is needed
          if (enableDispenserCheck && hasDispenserTasks()) {
            onStageChange("checking-dispensers");
          } else {
            onStageChange("security-checks");
          }
        } else {
          onStageChange("checking-utensils");
        }
        return;
      }

      if (!signalsResult && !checkingSignals) {
        handleCheckSignals();
      }
    } else if (flowStage === "checking-utensils") {
      if (!enableUtensilCheck || noPotsNeeded) {
        console.log("⏭️ Utensil check disabled or no pots needed, skipping...");
        // Check if dispenser check is needed
        if (enableDispenserCheck && hasDispenserTasks()) {
          onStageChange("checking-dispensers");
        } else {
          onStageChange("security-checks");
        }
        return;
      }

      if (Object.keys(utensilResults).length === 0 && !checkingUtensils) {
        // For merged recipes, call sequential operations once
        if (isMergedRecipes && performSequentialLidOperations) {
          console.log("🔄 Starting sequential operations for merged recipe...");

          // Set both utensils to checking status
          if (hasNP1) {
            setUtensilResults((prev) => ({ ...prev, np1: "checking" }));
            setLidCloseStatus((prev) => ({ ...prev, np1: "sequential" }));
          }
          if (hasNP2) {
            setUtensilResults((prev) => ({ ...prev, np2: "checking" }));
            setLidCloseStatus((prev) => ({ ...prev, np2: "sequential" }));
          }

          setCheckingUtensils(true);

          // Call sequential operations once for all utensils
          performSequentialLidOperations()
            .then(() => {
              // Mark all required utensils as successful
              if (hasNP1) {
                setUtensilResults((prev) => ({ ...prev, np1: true }));
                setLidCloseStatus((prev) => ({ ...prev, np1: "success" }));
              }
              if (hasNP2) {
                setUtensilResults((prev) => ({ ...prev, np2: true }));
                setLidCloseStatus((prev) => ({ ...prev, np2: "success" }));
              }
              setCheckingUtensils(false);
              console.log("✅ Sequential operations completed successfully");
            })
            .catch((error) => {
              console.error("❌ Sequential operations failed:", error);
              if (hasNP1) {
                setUtensilResults((prev) => ({ ...prev, np1: false }));
                setLidCloseStatus((prev) => ({ ...prev, np1: "error" }));
              }
              if (hasNP2) {
                setUtensilResults((prev) => ({ ...prev, np2: false }));
                setLidCloseStatus((prev) => ({ ...prev, np2: "error" }));
              }
              setCheckingUtensils(false);
            });
        } else {
          // For single recipes, check utensils individually
          if (hasNP1) {
            handleCheckUtensils("np1");
          }
          if (hasNP2) {
            setTimeout(() => handleCheckUtensils("np2"), 1000);
          }
        }
      }
    }
  }, [
    flowStage,
    signalsResult,
    checkingSignals,
    utensilResults,
    checkingUtensils,
    hasNP1,
    hasNP2,
    handleCheckSignals,
    handleCheckUtensils,
    isMergedRecipes,
    performSequentialLidOperations,
    enableSignalCheck,
    enableUtensilCheck,
    enableDispenserCheck,
    hasDispenserTasks,
    noPotsNeeded,
    onStageChange,
  ]);

  // Check if all utensils have been processed and auto-progress
  const checkAllUtensilsCompleted = useCallback(() => {
    const requiredUtensils = [];
    if (hasNP1) requiredUtensils.push("np1");
    if (hasNP2) requiredUtensils.push("np2");

    const allChecked = requiredUtensils.every((utensil) =>
      utensilResults.hasOwnProperty(utensil)
    );

    if (allChecked && !checkingUtensils && !closingLids) {
      const allUtensilsSuccessful = requiredUtensils.every(
        (utensil) => utensilResults[utensil] === true
      );
      const allLidsSuccessful = requiredUtensils.every(
        (utensil) => lidCloseStatus[utensil] === "success"
      );

      if (allUtensilsSuccessful && allLidsSuccessful) {
        console.log(
          "✅ All utensil checks completed successfully, auto-progressing..."
        );
        setTimeout(() => {
          // Check if dispenser check is needed
          if (enableDispenserCheck && hasDispenserTasks()) {
            onStageChange("checking-dispensers");
          } else {
            onStageChange("security-checks");
          }
        }, 2000);
      }
    }
  }, [
    hasNP1,
    hasNP2,
    utensilResults,
    lidCloseStatus,
    checkingUtensils,
    closingLids,
    enableDispenserCheck,
    hasDispenserTasks,
    onStageChange,
  ]);

  // Handle proceeding to next stage
  const handleProceedAnyway = async () => {
    if (flowStage === "checking-signals") {
      if (!enableUtensilCheck || noPotsNeeded) {
        // Check if dispenser check is needed
        if (enableDispenserCheck && hasDispenserTasks()) {
          onStageChange("checking-dispensers");
        } else {
          onStageChange("security-checks");
        }
      } else {
        onStageChange("checking-utensils");
      }
    } else if (flowStage === "checking-utensils") {
      console.log("Proceeding anyway, closing lids for safety...");

      const requiredUtensils = [];
      if (hasNP1) requiredUtensils.push("np1");
      if (hasNP2) requiredUtensils.push("np2");

      setClosingLids(true);

      try {
        for (const utensil of requiredUtensils) {
          if (lidCloseStatus[utensil] !== "success") {
            console.log(`🔒 Closing lid for ${utensil}...`);
            try {
              const closeCode = `async function start() {
  this.utensil = '${utensil}';
  await lidClose('${utensil}');
}
start();`;
              const closeResponse = await emitWithTimeout(
                "cook-now",
                closeCode,
                SOCKET_TIMEOUT
              );

              if (closeResponse === true) {
                setLidCloseStatus((prev) => ({
                  ...prev,
                  [utensil]: "success",
                }));
                console.log(`✅ Lid closed for ${utensil}`);
              } else {
                console.error(
                  `❌ Lid close failed for ${utensil}:`,
                  closeResponse
                );
                setLidCloseStatus((prev) => ({ ...prev, [utensil]: "failed" }));
              }
            } catch (err) {
              console.error(`❌ Error closing lid for ${utensil}:`, err);
              setLidCloseStatus((prev) => ({ ...prev, [utensil]: "failed" }));
            }
          }
        }

        // Recheck signals only if signal check is enabled
        if (enableSignalCheck) {
          console.log("🔍 Rechecking signals...");
          const signalResponse = await checkSignalsWithAnimation();
          setSignalsResult(signalResponse);
        }

        setClosingLids(false);
        setTimeout(() => {
          // Check if dispenser check is needed
          if (enableDispenserCheck && hasDispenserTasks()) {
            onStageChange("checking-dispensers");
          } else {
            onStageChange("security-checks");
          }
        }, 2000);
      } catch (err) {
        console.error("❌ Error during proceed anyway:", err);
        setClosingLids(false);
        setTimeout(() => {
          // Check if dispenser check is needed even after error
          if (enableDispenserCheck && hasDispenserTasks()) {
            onStageChange("checking-dispensers");
          } else {
            onStageChange("security-checks");
          }
        }, 1000);
      }
    }
  };

  // Handle retry
  const handleRetry = () => {
    if (flowStage === "checking-signals") {
      setSignalsResult(null);
      setSignalsError(null);
      setShowDetailedSignals(false);
      setEnhancedSignalCheck({
        isChecking: false,
        currentSensorIndex: -1,
        sensorResults: [],
        completed: false,
      });
      handleCheckSignals();
    } else if (flowStage === "checking-utensils") {
      setUtensilResults({});
      setUtensilErrors({});
      setLidCloseStatus({});
      if (hasNP1) {
        handleCheckUtensils("np1");
      }
      if (hasNP2) {
        setTimeout(() => handleCheckUtensils("np2"), 1000);
      }
    }
  };

  // Check for completion when utensil results change
  useEffect(() => {
    if (flowStage === "checking-utensils") {
      checkAllUtensilsCompleted();
    }
  }, [flowStage, utensilResults, checkAllUtensilsCompleted]);

  // Handle security check completion
  const handleSecurityCheckComplete = () => {
    const allChecksCompleted = securityChecks.every((check) =>
      completedSecurityChecks.has(check.id)
    );

    if (allChecksCompleted) {
      onFlowComplete(true);
    }
  };

  // Handle security check toggle
  const handleSecurityCheckToggle = (checkId) => {
    const newCompleted = new Set(completedSecurityChecks);
    if (newCompleted.has(checkId)) {
      newCompleted.delete(checkId);
    } else {
      newCompleted.add(checkId);
    }
    setCompletedSecurityChecks(newCompleted);
  };

  return (
    <Box sx={{ position: "relative", minHeight: "80vh" }}>
      {/* Simplified styles */}
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.7; }
            100% { opacity: 1; }
          }
        `}
      </style>

      {/* System Header */}
      <SystemHeader />

      {/* Main Content Area */}
      <Paper
        elevation={3}
        sx={{
          p: 4,
          borderRadius: 2,
          background: theme.palette.background.paper,
          border: `1px solid ${theme.palette.grey[200]}`,
        }}
      >
        {/* Stage-Specific Content */}
        {flowStage === "checking-signals" && (
          <Fade in={true} timeout={800}>
            <Box>
              <Typography
                variant="h6"
                sx={{ mb: 3, display: "flex", alignItems: "center" }}
              >
                <SpeedIcon sx={{ mr: 1 }} />
                Signal Check
                {!enableSignalCheck && (
                  <Chip
                    label="Disabled"
                    color="default"
                    size="small"
                    sx={{ ml: 2 }}
                  />
                )}
              </Typography>

              {!enableSignalCheck && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Signal check is disabled in settings
                  </Typography>
                  <Typography variant="body2">
                    Skipping signal verification and proceeding to next step...
                  </Typography>
                </Alert>
              )}

              {enableSignalCheck && (
                <>
                  {/* Enhanced Signal Display */}
                  <EnhancedSignalDisplay />

                  {checkingSignals && !enhancedSignalCheck.isChecking && (
                    <Alert severity="info" sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Checking kitchen sensors...
                      </Typography>
                      <LinearProgress sx={{ mt: 1 }} />
                    </Alert>
                  )}

                  {signalsResult && (
                    <Alert
                      severity={signalsResult.success ? "success" : "warning"}
                      sx={{ mb: 3 }}
                    >
                      <Typography variant="h6" sx={{ mb: 1 }}>
                        {signalsResult.success
                          ? "✅ All Systems OK"
                          : "⚠️ Issues Detected"}
                      </Typography>
                      <Typography variant="body2">
                        {signalsResult.message}
                      </Typography>

                      {showDetailedSignals &&
                        signalsResult.failedSensors?.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography
                              variant="subtitle2"
                              color="error"
                              sx={{ mb: 1 }}
                            >
                              Failed Sensors:
                            </Typography>
                            {signalsResult.failedSensors.map(
                              (sensor, index) => (
                                <Chip
                                  key={index}
                                  label={`${sensor.name} (Pos ${sensor.position})`}
                                  color="error"
                                  size="small"
                                  sx={{ mr: 1, mb: 1 }}
                                />
                              )
                            )}
                          </Box>
                        )}
                    </Alert>
                  )}

                  {!signalsResult?.success &&
                    !checkingSignals &&
                    !enhancedSignalCheck.isChecking && (
                      <Stack direction="row" spacing={2}>
                        <Button
                          variant="outlined"
                          startIcon={<RefreshIcon />}
                          onClick={handleRetry}
                        >
                          Retry Check
                        </Button>
                        <Button
                          variant="contained"
                          startIcon={<SkipNextIcon />}
                          onClick={handleProceedAnyway}
                          color="warning"
                        >
                          Continue Anyway
                        </Button>
                      </Stack>
                    )}
                </>
              )}
            </Box>
          </Fade>
        )}

        {flowStage === "checking-utensils" && !noPotsNeeded && (
          <Fade in={true} timeout={800}>
            <Box>
              <Typography
                variant="h6"
                sx={{ mb: 3, display: "flex", alignItems: "center" }}
              >
                <VisibilityIcon sx={{ mr: 1 }} />
                Utensil Check
                {!enableUtensilCheck && (
                  <Chip
                    label="Disabled"
                    color="default"
                    size="small"
                    sx={{ ml: 2 }}
                  />
                )}
              </Typography>

              {!enableUtensilCheck && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Utensil check is disabled in settings
                  </Typography>
                  <Typography variant="body2">
                    Skipping utensil verification and proceeding to next step...
                  </Typography>
                </Alert>
              )}

              {enableUtensilCheck && (
                <>
                  {/* Utensil Status Cards */}
                  <Stack spacing={2}>
                    {hasNP1 && (
                      <Card
                        sx={{
                          p: 2,
                          background:
                            utensilResults.np1 === true
                              ? `${theme.palette.success.main}10`
                              : theme.palette.background.paper,
                          border:
                            utensilResults.np1 === true
                              ? `1px solid ${theme.palette.success.main}40`
                              : `1px solid ${theme.palette.grey[300]}`,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <DeviceHubIcon sx={{ mr: 2 }} />
                          <Typography variant="subtitle1">
                            Cooking Pot 1 (NP1)
                          </Typography>
                          {checkingUtensils &&
                            !utensilResults.hasOwnProperty("np1") && (
                              <CircularProgress size={20} sx={{ ml: "auto" }} />
                            )}
                          {utensilResults.np1 === true && (
                            <CheckCircleIcon
                              sx={{ ml: "auto", color: "success.main" }}
                            />
                          )}
                        </Box>

                        {lidCloseStatus.np1 && (
                          <Box sx={{ mt: 1 }}>
                            <Chip
                              label={
                                lidCloseStatus.np1 === "sequential"
                                  ? "Processing..."
                                  : lidCloseStatus.np1 === "opening"
                                  ? "Opening Lid"
                                  : lidCloseStatus.np1 === "opened"
                                  ? "Checking Utensil"
                                  : lidCloseStatus.np1 === "success"
                                  ? "Complete"
                                  : "Issue Detected"
                              }
                              color={
                                lidCloseStatus.np1 === "success"
                                  ? "success"
                                  : lidCloseStatus.np1.includes("fail") ||
                                    lidCloseStatus.np1 === "error"
                                  ? "error"
                                  : "primary"
                              }
                              size="small"
                            />
                          </Box>
                        )}
                      </Card>
                    )}

                    {hasNP2 && (
                      <Card
                        sx={{
                          p: 2,
                          background:
                            utensilResults.np2 === true
                              ? `${theme.palette.success.main}10`
                              : theme.palette.background.paper,
                          border:
                            utensilResults.np2 === true
                              ? `1px solid ${theme.palette.success.main}40`
                              : `1px solid ${theme.palette.grey[300]}`,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <DeviceHubIcon sx={{ mr: 2 }} />
                          <Typography variant="subtitle1">
                            Cooking Pot 2 (NP2)
                          </Typography>
                          {checkingUtensils &&
                            !utensilResults.hasOwnProperty("np2") && (
                              <CircularProgress size={20} sx={{ ml: "auto" }} />
                            )}
                          {utensilResults.np2 === true && (
                            <CheckCircleIcon
                              sx={{ ml: "auto", color: "success.main" }}
                            />
                          )}
                        </Box>

                        {lidCloseStatus.np2 && (
                          <Box sx={{ mt: 1 }}>
                            <Chip
                              label={
                                lidCloseStatus.np2 === "sequential"
                                  ? "Processing..."
                                  : lidCloseStatus.np2 === "opening"
                                  ? "Opening Lid"
                                  : lidCloseStatus.np2 === "opened"
                                  ? "Checking Utensil"
                                  : lidCloseStatus.np2 === "success"
                                  ? "Complete"
                                  : "Issue Detected"
                              }
                              color={
                                lidCloseStatus.np2 === "success"
                                  ? "success"
                                  : lidCloseStatus.np2.includes("fail") ||
                                    lidCloseStatus.np2 === "error"
                                  ? "error"
                                  : "primary"
                              }
                              size="small"
                            />
                          </Box>
                        )}
                      </Card>
                    )}
                  </Stack>

                  {!checkingUtensils &&
                    !closingLids &&
                    Object.keys(utensilResults).length > 0 &&
                    !Object.values(utensilResults).every((r) => r === true) && (
                      <Box sx={{ mt: 3 }}>
                        <Stack direction="row" spacing={2}>
                          <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={handleRetry}
                          >
                            Retry Check
                          </Button>
                          <Button
                            variant="contained"
                            startIcon={<SkipNextIcon />}
                            onClick={handleProceedAnyway}
                            color="warning"
                          >
                            Continue Anyway
                          </Button>
                        </Stack>
                      </Box>
                    )}
                </>
              )}
            </Box>
          </Fade>
        )}

        {flowStage === "checking-dispensers" && (
          <Fade in={true} timeout={800}>
            <Box>
              <Typography
                variant="h6"
                sx={{ mb: 3, display: "flex", alignItems: "center" }}
              >
                <Sensors sx={{ mr: 1, color: "#ff9800" }} />
                Dispenser Check
                {!enableDispenserCheck && (
                  <Chip
                    label="Disabled"
                    color="default"
                    size="small"
                    sx={{ ml: 2 }}
                  />
                )}
              </Typography>

              {!enableDispenserCheck && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Dispenser check is disabled in settings
                  </Typography>
                  <Typography variant="body2">
                    Skipping dispenser testing and proceeding to next step...
                  </Typography>
                </Alert>
              )}

              {!hasDispenserTasks() && enableDispenserCheck && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    No dispenser tasks found in recipe
                  </Typography>
                  <Typography variant="body2">
                    This recipe doesn't require grain or spice dispensing.
                    Skipping dispenser test...
                  </Typography>
                </Alert>
              )}

              {enableDispenserCheck && hasDispenserTasks() && (
                <>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    Testing with first dispense ingredient to ensure dispensers
                    are working properly...
                  </Typography>

                  {/* Dispenser Status Cards */}
                  <Stack spacing={2}>
                    {getFirstGrainTask() && (
                      <Card
                        sx={{
                          p: 2,
                          background:
                            dispenserCheckStatus?.grainStatus === "success"
                              ? `${theme.palette.success.main}10`
                              : dispenserCheckStatus?.grainStatus === "failed"
                              ? `${theme.palette.error.main}10`
                              : theme.palette.background.paper,
                          border:
                            dispenserCheckStatus?.grainStatus === "success"
                              ? `1px solid ${theme.palette.success.main}40`
                              : dispenserCheckStatus?.grainStatus === "failed"
                              ? `1px solid ${theme.palette.error.main}40`
                              : `1px solid ${theme.palette.grey[300]}`,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Box sx={{ mr: 2, fontSize: 24 }}>🌾</Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              Grain Dispenser
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Testing with {getFirstGrainTask()?.item_name} (
                              {getFirstGrainTask()?.quantity}g) from Box{" "}
                              {getFirstGrainTask()?.position}
                            </Typography>
                          </Box>
                          {dispenserCheckStatus?.grainStatus === "checking" && (
                            <CircularProgress size={20} sx={{ ml: "auto" }} />
                          )}
                          {dispenserCheckStatus?.grainStatus === "success" && (
                            <Chip
                              label="Dispenser OK"
                              color="success"
                              icon={<CheckCircleIcon />}
                              sx={{ ml: "auto" }}
                            />
                          )}
                          {dispenserCheckStatus?.grainStatus === "failed" && (
                            <Chip
                              label="Check Failed"
                              color="error"
                              icon={<ErrorIcon />}
                              sx={{ ml: "auto" }}
                            />
                          )}
                        </Box>
                      </Card>
                    )}

                    {getFirstSpiceTask() && (
                      <Card
                        sx={{
                          p: 2,
                          background:
                            dispenserCheckStatus?.masalaStatus === "success"
                              ? `${theme.palette.success.main}10`
                              : dispenserCheckStatus?.masalaStatus === "failed"
                              ? `${theme.palette.error.main}10`
                              : theme.palette.background.paper,
                          border:
                            dispenserCheckStatus?.masalaStatus === "success"
                              ? `1px solid ${theme.palette.success.main}40`
                              : dispenserCheckStatus?.masalaStatus === "failed"
                              ? `1px solid ${theme.palette.error.main}40`
                              : `1px solid ${theme.palette.grey[300]}`,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Box sx={{ mr: 2, fontSize: 24 }}>🌶️</Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              Masala Dispenser
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Testing with {getFirstSpiceTask()?.item_name} (
                              {getFirstSpiceTask()?.quantity}g) from Box{" "}
                              {getFirstSpiceTask()?.position}
                            </Typography>
                          </Box>
                          {dispenserCheckStatus?.masalaStatus ===
                            "checking" && (
                            <CircularProgress size={20} sx={{ ml: "auto" }} />
                          )}
                          {dispenserCheckStatus?.masalaStatus === "success" && (
                            <Chip
                              label="Dispenser OK"
                              color="success"
                              icon={<CheckCircleIcon />}
                              sx={{ ml: "auto" }}
                            />
                          )}
                          {dispenserCheckStatus?.masalaStatus === "failed" && (
                            <Chip
                              label="Check Failed"
                              color="error"
                              icon={<ErrorIcon />}
                              sx={{ ml: "auto" }}
                            />
                          )}
                        </Box>
                      </Card>
                    )}
                  </Stack>

                  {dispenserCheckStatus?.isChecking && (
                    <Alert severity="info" sx={{ mt: 3 }}>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Testing dispensers...
                      </Typography>
                      <Typography variant="body2">
                        Please wait while we test with first dispense ingredient
                        from each dispenser.
                      </Typography>
                      <LinearProgress sx={{ mt: 1 }} />
                    </Alert>
                  )}

                  {dispenserCheckStatus?.error && (
                    <Alert severity="error" sx={{ mt: 3 }}>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Dispenser Test Error
                      </Typography>
                      <Typography variant="body2">
                        {dispenserCheckStatus.error}
                      </Typography>
                    </Alert>
                  )}

                  {dispenserCheckStatus?.completed &&
                    !dispenserCheckStatus.isChecking && (
                      <>
                        {(dispenserCheckStatus.grainStatus === "success" ||
                          !getFirstGrainTask()) &&
                        (dispenserCheckStatus.masalaStatus === "success" ||
                          !getFirstSpiceTask()) ? (
                          <Alert severity="success" sx={{ mt: 3 }}>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                              ✅ All Dispensers Working
                            </Typography>
                            <Typography variant="body2">
                              Dispenser tests completed successfully. Proceeding
                              to safety checks...
                            </Typography>
                          </Alert>
                        ) : (
                          <Box sx={{ mt: 3 }}>
                            <Alert severity="warning" sx={{ mb: 2 }}>
                              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                ⚠️ Dispenser Issues Detected
                              </Typography>
                              <Typography variant="body2">
                                {(() => {
                                  const failedDispensers = [];
                                  if (
                                    dispenserCheckStatus?.grainStatus ===
                                    "failed"
                                  ) {
                                    failedDispensers.push("Grain");
                                  }
                                  if (
                                    dispenserCheckStatus?.masalaStatus ===
                                    "failed"
                                  ) {
                                    failedDispensers.push("Masala");
                                  }
                                  return `${failedDispensers.join(
                                    " and "
                                  )} dispenser${
                                    failedDispensers.length > 1 ? "s" : ""
                                  } failed the test. You can retry failed dispensers or continue anyway.`;
                                })()}
                              </Typography>
                            </Alert>
                            <Stack direction="row" spacing={2}>
                              <Button
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={() => {
                                  // Smart retry - only retry failed dispensers
                                  console.log(
                                    "Retrying failed dispensers only..."
                                  );
                                  if (onDispenserRetry) {
                                    onDispenserRetry();
                                  } else {
                                    // Fallback to regular retry
                                    onStageChange("checking-dispensers");
                                  }
                                }}
                              >
                                {(() => {
                                  const failedCount =
                                    (dispenserCheckStatus?.grainStatus ===
                                    "failed"
                                      ? 1
                                      : 0) +
                                    (dispenserCheckStatus?.masalaStatus ===
                                    "failed"
                                      ? 1
                                      : 0);
                                  return failedCount === 1
                                    ? "Retry Failed Dispenser"
                                    : "Retry Failed Dispensers";
                                })()}
                              </Button>
                              <Button
                                variant="contained"
                                startIcon={<SkipNextIcon />}
                                onClick={() => {
                                  console.log("Skipping dispenser check...");
                                  onStageChange("security-checks");
                                }}
                                color="warning"
                              >
                                Continue Anyway
                              </Button>
                            </Stack>
                          </Box>
                        )}
                      </>
                    )}
                </>
              )}
            </Box>
          </Fade>
        )}

        {flowStage === "security-checks" && (
          <Fade in={true} timeout={800}>
            <Box>
              <Typography
                variant="h6"
                sx={{ mb: 3, display: "flex", alignItems: "center" }}
              >
                <ShieldIcon sx={{ mr: 1 }} />
                Safety Confirmation
              </Typography>

              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Please confirm the following safety checks:
              </Typography>

              <Stack spacing={2} sx={{ mb: 4 }}>
                {securityChecks.map((check) => (
                  <Card
                    key={check.id}
                    sx={{
                      p: 2,
                      background: completedSecurityChecks.has(check.id)
                        ? `${theme.palette.success.main}10`
                        : theme.palette.background.paper,
                      border: completedSecurityChecks.has(check.id)
                        ? `1px solid ${theme.palette.success.main}40`
                        : check.critical
                        ? `1px solid ${theme.palette.error.main}40`
                        : `1px solid ${theme.palette.grey[300]}`,
                      cursor: "pointer",
                    }}
                    onClick={() => handleSecurityCheckToggle(check.id)}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <input
                        type="checkbox"
                        checked={completedSecurityChecks.has(check.id)}
                        onChange={() => handleSecurityCheckToggle(check.id)}
                        style={{
                          marginRight: 16,
                          transform: "scale(1.2)",
                          accentColor: theme.palette.primary.main,
                        }}
                      />
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                          flex: 1,
                          color: check.critical ? "error.main" : "text.primary",
                        }}
                      >
                        {check.message}
                      </Typography>
                      {check.critical && (
                        <Chip
                          label="Critical"
                          color="error"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Card>
                ))}
              </Stack>

              <Button
                variant="contained"
                size="large"
                startIcon={<PlayArrowIcon />}
                onClick={handleSecurityCheckComplete}
                disabled={
                  !securityChecks.every((check) =>
                    completedSecurityChecks.has(check.id)
                  )
                }
                fullWidth
                sx={{
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: 600,
                }}
              >
                Start Cooking
              </Button>
            </Box>
          </Fade>
        )}
      </Paper>

      {/* No Pots Needed Info */}
      {noPotsNeeded && flowStage === "checking-signals" && signalsResult && (
        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            No cooking pots required for this recipe
          </Typography>
          <Typography variant="body2">Skipping utensil check...</Typography>
        </Alert>
      )}
    </Box>
  );
};

export default KitchenFlow;
